import type { ApiError, AuthSession, ErrorCode } from '@pm/schemas';

/** Erro de API com código estável, para a UI decidir o que mostrar. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type Json = Record<string, unknown> | unknown[] | null;

interface RequestOptions {
  body?: Json;
  /** Desliga a tentativa automática de refresh (usada pelo próprio refresh/login). */
  noAuthRetry?: boolean;
  signal?: AbortSignal;
}

/**
 * Cliente HTTP: base /api/v1 (proxy do Vite em dev, Caddy em prod → mesma origem, cookie funciona).
 * Access token vive em memória; refresh acontece via cookie HttpOnly.
 * 401 dispara UM refresh (deduplicado) e repete a requisição.
 */
class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<AuthSession | null> | null = null;
  private onUnauthenticated: (() => void) | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  setUnauthenticatedHandler(handler: (() => void) | null): void {
    this.onUnauthenticated = handler;
  }

  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, opts);
  }
  post<T>(path: string, body?: Json, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...opts, body });
  }
  patch<T>(path: string, body?: Json, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...opts, body });
  }
  put<T>(path: string, body?: Json, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...opts, body });
  }
  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }

  /**
   * Renova a sessão pelo cookie. Chamadas concorrentes compartilham a mesma promise, e abas
   * diferentes renovam uma de cada vez (Web Locks): duas renovações simultâneas com o mesmo
   * cookie pareceriam reuso de token roubado.
   */
  refresh(): Promise<AuthSession | null> {
    if (!this.refreshing) {
      const call = () => this.request<AuthSession>('POST', '/auth/refresh', { noAuthRetry: true });
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      // A trava fica com esta aba até a resposta chegar (o callback devolve a própria chamada).
      const run = locks
        ? new Promise<AuthSession>((resolve, reject) => {
            locks.request('pm-auth-refresh', () => call().then(resolve, reject)).catch(reject);
          })
        : call();
      this.refreshing = run
        .then((session) => {
          this.accessToken = session.access_token;
          return session;
        })
        .catch(() => {
          this.accessToken = null;
          return null;
        })
        .finally(() => {
          this.refreshing = null;
        });
    }
    return this.refreshing;
  }

  private async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await this.raw(method, path, opts);

    if (res.status === 401 && !opts.noAuthRetry) {
      const session = await this.refresh();
      if (session) {
        const retried = await this.raw(method, path, opts);
        return this.parse<T>(retried);
      }
      this.onUnauthenticated?.();
    }
    return this.parse<T>(res);
  }

  private raw(method: string, path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    return fetch(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json = safeJson(text);
    if (!res.ok) {
      const err = (json as ApiError | null)?.error;
      throw new ApiRequestError(
        res.status,
        err?.code ?? 'INTERNAL_ERROR',
        err?.message ?? 'Erro inesperado.',
        err?.details,
      );
    }
    return (json as { data: T }).data;
  }
}

function safeJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export const api = new ApiClient();
